import { Box, IconButton, InputLabel, Typography } from '@mui/material'

import CloseIcon from '@mui/icons-material/Close'
import ProgressBar from 'components/DataGrid/components/ProgressBar'
import { getProgressValue } from 'components/MTB/grid/getMtbProgressData'
import { FC, useMemo } from 'react'
import { useLocation } from 'react-router-dom'
import { MonthlyProgressItem } from 'types'

type Entities = {
  name: string
  isCreative?: boolean
}

type Props = {
  entity: Entities
  isExpanded?: boolean
  progressData?: MonthlyProgressItem[]
  onClose: () => void
}

const ExpandCollapseBar: FC<Props> = ({ entity, isExpanded, progressData = null, onClose }) => {
  const location = useLocation()

  const { label, progress } = useMemo(() => {
    if (!progressData) return { label: '', progress: 0 }

    const pathname = location?.pathname ?? ''
    // Convert decimal (0-1) to percentage (0-100) for ProgressBar component
    if (pathname.includes('am')) {
      return { label: 'AM Progress', progress: Math.round(getProgressValue('AM', progressData) * 100) }
    }
    if (pathname.includes('dms')) {
      return { label: 'DMS Progress', progress: Math.round(getProgressValue('DMS', progressData) * 100) }
    }
    if (pathname.includes('seo')) {
      return { label: 'SEO Progress', progress: Math.round(getProgressValue('SEO', progressData) * 100) }
    }
    return { label: 'MTB Progress', progress: Math.round(getProgressValue('OVERALL', progressData) * 100) }
  }, [progressData, location?.pathname])

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        width: '100%',
        gap: 2,
      }}
    >
      {/* Organization Name */}
      <Typography
        variant="h6"
        sx={{
          flex: '0 0 auto',
          textAlign: 'left',
          color: entity?.isCreative ? 'icon.projects' : 'icon.nco',
          whiteSpace: 'nowrap',
        }}
      >
        {entity?.name}
      </Typography>

      {/* Progress Bar - takes available space */}
      {progressData && (
        <Box sx={{ flex: '1 1 auto', maxWidth: 300, minWidth: 150 }}>
          <InputLabel sx={{ fontSize: 11, mb: 0.5 }}>{label}</InputLabel>
          <ProgressBar progress={progress} />
        </Box>
      )}

      {/* Expand info text - only show when collapsed */}
      {!isExpanded && (
        <Typography
          sx={{
            flex: '0 0 auto',
            color: 'text.disabled',
            fontSize: 13,
            fontStyle: 'italic',
            whiteSpace: 'nowrap',
          }}
        >
          Expand for more info...
        </Typography>
      )}

      {/* Close Button */}
      <IconButton onClick={onClose} sx={{ flex: '0 0 auto' }}>
        <CloseIcon fontSize="medium" />
      </IconButton>
    </Box>
  )
}
export default ExpandCollapseBar
